import { CreateCustomerBody } from "../models/bodies/CreateCustomerBody";
import { SaveDesignSnapshotBody } from "../models/bodies/SaveDesignSnapshotBody";
import { UpdateCustomerBody } from "../models/bodies/UpdateCustomerBody";
import { DesignSnapshotResponse } from "../models/responses/DesignSnapshotResponse";
import { LogoPriceDto, OrderDto } from "../models/responses/OrderDto";
import { ProfileResponse } from "../models/responses/ProfileResponse";
import { UrlResponse } from "../models/responses/UrlResponse";
import { useAuthorizedFetch } from "./AuthorizedFetch";
import { UpdateDesignBody } from "../models/bodies/UpdateDesignBody";
import { TextLogoResponse } from "../models/responses/TextLogoResponse";
import { CreateTextLogoBody } from "../models/bodies/CreateTextLogoBody";
import { ManufacturerDto } from "../models/responses/ManufacturerDto";
import { DesignSummaryResponse } from "../models/responses/DesignSummaryResponse";
import { CreateManufacturerBody } from "../models/bodies/CreateManufacturerBody";
import { CreateUserBody } from "../models/bodies/CreateUserBody";
import { CreateMaskQueueCount } from "../models/responses/CreateMaskQueueCount";
import { UpdateStyleBody } from "../models/bodies/UpdateStyleBody";
import { PageQuery, toQueryParam } from "../../utils/models/pageQuery";
import { BaseLogo, ImageLogo } from "../../core/models/entities/BaseLogo";
import { Organization } from "../../core/models/entities/Organization";
import { enviroment } from "../../enviroment";
import { CreateDesignDto } from "../../core/models/dtos/CreateDesignDto";
import { DesignDto } from "../models/responses/DesignDto";
import { PagedResponse } from "../models/responses/pagedResponse";
import { LogoDto } from "../models/responses/LogoDto";
import { OrganizationDto } from "../models/responses/OrganizationDto";
import { UpdateProfileDto } from "../../core/models/entities/UpdateProfileDto";
import { StyleDto } from "../models/responses/StyleDto";
import { ApprovalSubmission } from "../models/bodies/ApprovalSubmission";
import { LogoOptionsDto } from "../models/responses/logoOptionsDto";
import { TransferDto } from "../models/responses/transferOptionsDto";
import {
  ChangeLogoColors,
  InitiateApproval,
} from "../../presentation/screens/logo-list/hooks/useLogoActionsHandler";
import { LogoCommit } from "../models/bodies/LogoCommit";
import { Design } from "../../core/models/entities/Design";
import { User } from "../../core/models/entities/User";
import { CommissionProfile } from "../../core/models/entities/Commission";
import { CommissionProfileDto } from "../models/responses/CommissionDto";
import {
  CreateOrderDto,
  CreateOrderPreferencesDto,
  UpdateOrderDto,
} from "../../core/models/dtos/CreateOrderDto";
import {
  DeliveryType,
  Order,
  OrderLineDesign,
  Packaging,
} from "../../core/models/entities/Order";
import { Verdict } from "../../presentation/components/ApprovalVoting/approval-voting";
import { CountryDto } from "../models/responses/CountryDto";
import { CreateImageLogoBody } from "../models/bodies/CreateImageLogoBody";
import {
  CartItemDesign,
  CartItemDesignInstance,
  CartSummary,
  CartSummaryRequest,
} from "../../presentation/screens/order-create/models/CartItem";
import { CreateDesignTemplateDto } from "../../core/models/dtos/CreateDesignTemplateDto";
import { CreateNameLogoBody } from "../models/bodies/CreateNameLogoBody";
import { CreateNumberLogoBody } from "../models/bodies/CreateNumberLogoBody";
import { ChangeStyleBody, ChangeTitleBody } from "../models/bodies/ChangeBody";
import {
  DesignTemplate,
  LogoTemplate,
} from "../../core/models/entities/DesignTemplate";
import { ExcludedEmailDomainDto } from "../models/responses/ExcludedEmailDomainDto";
import { AddExcludedEmailDomainDto } from "../models/bodies/AddExcludedEmailDomainDto";
import { DeleteExcludedEmailDomainDto } from "../models/bodies/DeleteExcludedEmailDomainDto";
import { OrganizationManufacturerAccessDto } from "../models/responses/OrganizationManufacturerAccessDto";
import {
  StartPriceImportRequestDto,
  StartPriceImportResponseDto,
  PriceImportStatusResponseDto,
  PriceImportHistoryResponseDto,
  CancelPriceImportResponseDto,
} from "../models/responses/PriceImportDto";
import {
  BulkLogoImportRequestDto,
  BulkLogoImportResponseDto,
  LogoSerialsResponseDto,
  LogoSerialsFilterParams,
} from "../models/responses/LogoImportDto";
import {
  BulkDesignImportRequestDto,
  BulkDesignImportResponseDto,
  DesignSerialsResponseDto,
  DesignSerialsFilterParams,
} from "../models/responses/DesignImportDto";

// FIXME: Should use current user's organization id instead of the selected customer's id
// FIXME: Please find the above comment attached multiple places in the code below.
//  The problem is general and should be fixed across API calls where relevant.
//  Please ask with backend dev for exact requirements before changing anything.
export function useApiClient() {
  const API_URL = enviroment.apiUrl;

  const { authFetch } = useAuthorizedFetch();

  async function getAllStyles(
    query: PageQuery,
  ): Promise<PagedResponse<StyleDto>> {
    const queryString = toQueryParam(query);
    const response = await authFetch(`${API_URL}/styles${queryString}`);

    return await response.json();
  }

  async function getAllDesigns(
    query: PageQuery,
    includePrices: boolean = true,
  ): Promise<PagedResponse<DesignDto>> {
    const queryString = toQueryParam(query);
    const includePricesParam = `&includePrices=${includePrices}`;
    const response = await authFetch(
      `${API_URL}/organizations/designs${queryString}${includePricesParam}`,
    );
    return await response.json();
  }

  async function getAllOrders(
    query: PageQuery,
  ): Promise<PagedResponse<OrderDto>> {
    const response = await authFetch(
      `${API_URL}/organizations/orders${toQueryParam(query)}`,
    );
    return await response.json();
  }

  async function getAllManufacturers(
    orgId?: number,
  ): Promise<PagedResponse<ManufacturerDto>> {
    let orgIdQuery = "";

    if (orgId) {
      orgIdQuery = `orgId=${orgId}&`;
    }

    const response = await authFetch(
      `${API_URL}/manufacturers?${orgIdQuery}pageSize=0`,
    );
    return await response.json();
  }

  async function getManufacturer(
    manufacturerId: number,
  ): Promise<ManufacturerDto> {
    const response = await authFetch(
      `${API_URL}/manufacturers/${manufacturerId}`,
    );
    return await response.json();
  }

  async function getDesign(
    organizationId: number,
    designId: number,
  ): Promise<DesignDto> {
    const response = await authFetch(
      `${API_URL}/organizations/${organizationId}/designs/${designId}`,
    );
    return await response.json();
  }

  async function createDesign(body: CreateDesignDto): Promise<DesignDto> {
    const response = await authFetch(
      `${API_URL}/organizations/${body.orgId}/designs`,
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    );
    return await response.json();
  }

  async function updateDesign(
    organizationId: number,
    designId: number,
    body: UpdateDesignBody,
  ): Promise<DesignDto> {
    const response = await authFetch(
      `${API_URL}/organizations/${organizationId}/designs/${designId}`,
      {
        method: "PUT",
        body: JSON.stringify(body),
      },
    );
    return await response.json();
  }

  async function createImageLogo(body: CreateImageLogoBody): Promise<LogoDto> {
    const response = await authFetch(`${API_URL}/organizations/image-logo`, {
      method: "POST",
      body: JSON.stringify(body),
    });

    return response.json();
  }

  async function uploadFileImageLogo(
    logo: File,
    customerId: number,
    logoId: number,
  ): Promise<LogoDto> {
    const formData = new FormData();
    formData.append("file", logo);

    const response = await authFetch(
      `${API_URL}/organizations/${customerId}/image-logo/${logoId}`,
      {
        method: "PUT",
        body: formData,
      },
    );

    return response.json();
  }

  async function createAndUploadImageLogo(
    logo: File,
    orgId: number,
  ): Promise<LogoDto> {
    const formData = new FormData();
    formData.append("file", logo);

    const response = await authFetch(
      `${API_URL}/organizations/${orgId}/image-logo`,
      {
        method: "POST",
        body: formData,
      },
    );

    return response.json();
  }

  async function createOrder(
    orgId: number,
    createOrder: CreateOrderDto,
  ): Promise<OrderDto> {
    const response = await authFetch(
      `${API_URL}/organizations/${orgId}/orders`,
      {
        method: "POST",
        body: JSON.stringify(createOrder),
      },
    );

    return await response.json();
  }

  async function updateOrder(
    orgId: number,
    orderId: number,
    updateOrder: UpdateOrderDto,
  ): Promise<OrderDto> {
    const response = await authFetch(
      `${API_URL}/organizations/${orgId}/orders/${orderId}`,
      {
        method: "PUT",
        body: JSON.stringify(updateOrder),
      },
    );

    return response.json();
  }

  async function getOrderPrices(
    orgId: number | undefined,
    createOrder: CreateOrderDto,
  ): Promise<LogoPriceDto[]> {
    if (orgId === undefined) {
      return [];
    }

    const response = await authFetch(
      `${API_URL}/organizations/${orgId}/orderprices`,
      {
        method: "POST",
        body: JSON.stringify(createOrder),
      },
    );

    return response.json();
  }

  async function commitOrder(
    orgId: number,
    orderId: number,
    createOrder: CreateOrderPreferencesDto,
  ): Promise<OrderDto> {
    const response = await authFetch(
      `${API_URL}/organizations/${orgId}/orders/${orderId}/commit`,
      {
        method: "POST",
        body: JSON.stringify(createOrder),
      },
    );

    return response.json();
  }

  async function summarizeOrder(
    orgId: number,
    body: CartSummaryRequest,
  ): Promise<CartSummary> {
    const response = await authFetch(
      `${API_URL}/organizations/${orgId}/orders/prices`,
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    );

    return response.json();
  }

  async function createTextLogo(
    orgId: number,
    body: CreateTextLogoBody,
  ): Promise<LogoDto> {
    const response = await authFetch(
      `${API_URL}/organizations/${orgId}/text-logo`,
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    );
    return response.json();
  }

  async function createPlaceholderNameLogo(
    orgId: number,
    body: CreateNameLogoBody,
  ): Promise<LogoDto> {
    const response = await authFetch(
      `${API_URL}/organizations/${orgId}/name-logo`,
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    );
    return response.json();
  }

  async function createPlaceholderNumberLogo(
    orgId: number,
    body: CreateNumberLogoBody,
  ): Promise<LogoDto> {
    const response = await authFetch(
      `${API_URL}/organizations/${orgId}/number-logo?orgId=${orgId}`,
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    );
    return response.json();
  }

  async function generateTextLogoPreview(
    orgId: number,
    body: CreateTextLogoBody,
  ): Promise<string> {
    const response = await authFetch(
      `${API_URL}/organizations/${orgId}/text-logo-preview`,
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    );

    return await response.text();
  }

  async function generateNumberLogoPreview(
    orgId: number,
    body: CreateNumberLogoBody,
  ): Promise<string> {
    const response = await authFetch(
      `${API_URL}/organizations/${orgId}/number-logo-preview?orgId=${orgId}`,
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    );

    return await response.text();
  }

  async function updateLogo(orgId: number, logo: BaseLogo): Promise<BaseLogo> {
    const response = await authFetch(
      `${API_URL}/organizations/${orgId}/logos/${logo.id}/update`,
      {
        method: "PUT",
        body: JSON.stringify(logo),
      },
    );

    return response.json();
  }

  async function resizeLogo(orgId: number, logo: BaseLogo): Promise<BaseLogo> {
    const response = await authFetch(
      `${API_URL}/organizations/${orgId}/logos/${logo.id}/resize`,
      {
        method: "PUT",
        body: JSON.stringify(logo),
      },
    );

    return response.json();
  }

  async function copyLogo(orgId: number, logoId: number): Promise<BaseLogo> {
    const response = await authFetch(
      `${API_URL}/organizations/${orgId}/logos/${logoId}/copy`,
      {
        method: "POST",
      },
    );

    return response.json();
  }

  async function commitLogo(
    logo: BaseLogo,
    logoCommit: LogoCommit,
  ): Promise<BaseLogo> {
    const logoId = logo.id;
    const orgId = logo.customer.id;

    const url = `${API_URL}/organizations/${orgId}/logos/${logoId}/commit`;

    const response = await authFetch(url, {
      method: "PUT",
      body: JSON.stringify(logoCommit),
    });

    return response.json();
  }

  async function commitDesign(design: Design): Promise<DesignDto> {
    const designId = design.id;
    const orgId = design.customer.id;

    const url = `${API_URL}/organizations/${orgId}/designs/${designId}/commit`;

    const response = await authFetch(url, {
      method: "PUT",
    });

    return response.json();
  }

  async function skipApprovalDesign(design: Design): Promise<DesignDto> {
    const designId = design.id;
    const orgId = design.customer.id;

    const url = `${API_URL}/organizations/${orgId}/designs/${designId}/approval-skip`;

    const response = await authFetch(url, {
      method: "POST",
    });

    return response.json();
  }

  async function copyDesign(
    orgId: number,
    designId: number,
  ): Promise<DesignDto> {
    const response = await authFetch(
      `${API_URL}/organizations/${orgId}/designs/${designId}/copy`,
      {
        method: "POST",
      },
    );

    return response.json();
  }

  async function getAllTextLogos(): Promise<TextLogoResponse[]> {
    return [];
  }

  async function getDesignSnapshot(
    organizationId: number,
    designId: number,
  ): Promise<DesignSnapshotResponse> {
    const response = await authFetch(
      `${API_URL}/organizations/${organizationId}/designs/${designId}/snapshot`,
    );
    return await response.json();
  }

  async function getDesignPriceSummary(
    organizationId: number,
    designId: number,
  ): Promise<DesignSummaryResponse> {
    const response = await authFetch(
      `${API_URL}/organizations/${organizationId}/designs/${designId}/summary`,
    );
    return await response.json();
  }

  async function getOrder(
    organizationId: number,
    orderId: number,
  ): Promise<OrderDto> {
    const response = await authFetch(
      `${API_URL}/organizations/${organizationId}/orders/${orderId}`,
    );

    return await response.json();
  }

  async function saveDesignSnapshot(
    organizationId: number,
    designId: number,
    body: SaveDesignSnapshotBody,
  ): Promise<void> {
    await authFetch(
      `${API_URL}/organizations/${organizationId}/designs/${designId}/snapshot`,
      {
        method: "PUT",
        body: JSON.stringify(body),
      },
    );
  }

  async function submitDesign(
    organizationId: number,
    designId: number,
    submission: ApprovalSubmission,
  ): Promise<void> {
    await authFetch(
      `${API_URL}/organizations/${organizationId}/designs/${designId}/submit`,
      {
        method: "PUT",
        body: JSON.stringify(submission),
      },
    );
  }

  async function getVendor(id: number): Promise<OrganizationDto> {
    const response = await authFetch(`${API_URL}/organizations/vendor/${id}`);
    return await response.json();
  }

  async function getOrganization(id: number): Promise<OrganizationDto> {
    if (id == null || id === 0) {
      throw new Error("Invalid organization ID");
    }
    const response = await authFetch(`${API_URL}/organizations/${id}`);
    return await response.json();
  }

  async function getCustomer(id: number): Promise<OrganizationDto> {
    const response = await authFetch(`${API_URL}/organizations/customer/${id}`);
    return await response.json();
  }

  async function getAllVendors(
    query: PageQuery,
  ): Promise<PagedResponse<OrganizationDto>> {
    const queryString = toQueryParam(query);
    const response = await authFetch(
      `${API_URL}/organizations/Vendors${queryString}`,
    );
    return await response.json();
  }

  async function getAllCustomers(
    query: PageQuery,
  ): Promise<PagedResponse<OrganizationDto>> {
    const queryString = toQueryParam(query);
    const response = await authFetch(
      `${API_URL}/organizations/Customers${queryString}`,
    );
    return await response.json();
  }

  async function createOrganization(
    body: CreateCustomerBody,
  ): Promise<OrganizationDto> {
    const response = await authFetch(`${API_URL}/organizations`, {
      method: "POST",
      body: JSON.stringify(body),
    });

    return await response.json();
  }

  async function updateOrganization(
    customerId: number,
    body: UpdateCustomerBody,
  ): Promise<OrganizationDto> {
    const response = await authFetch(`${API_URL}/organizations/${customerId}`, {
      body: JSON.stringify(body),
      method: "PUT",
    });

    return await response.json();
  }

  async function deleteOrganization(id: number): Promise<void> {
    await authFetch(`${API_URL}/organizations/${id}`, {
      method: "DELETE",
    });
  }

  async function createCustomer(
    customer: Organization,
  ): Promise<OrganizationDto> {
    const response = await authFetch(`${API_URL}/organizations/customers`, {
      method: "POST",
      body: JSON.stringify(customer),
    });

    return await response.json();
  }

  async function updateCustomer(
    customer: Organization,
  ): Promise<OrganizationDto> {
    const response = await authFetch(
      `${API_URL}/organizations/${customer.id}`,
      {
        body: JSON.stringify(customer),
        method: "PUT",
      },
    );

    return await response.json();
  }

  async function deleteCustomer(customer: Organization): Promise<void> {
    await authFetch(`${API_URL}/organizations/${customer.id}`, {
      method: "DELETE",
    });
  }

  async function getLogos(
    orgId: number,
    query: PageQuery,
  ): Promise<PagedResponse<LogoDto>> {
    const queryString = toQueryParam(query);
    const response = await authFetch(`${API_URL}/logos/data`);

    return await response.json();
  }

  async function getAllLogos(
    query: PageQuery,
    type?: string,
  ): Promise<PagedResponse<LogoDto>> {
    const typeParameter = type ? "/" + type : "";
    const queryString = toQueryParam(query);
    const response = await authFetch(
      `${API_URL}/logos${typeParameter}${queryString}`,
    );
    const data = await response.json();
    return data;
  }

  async function getAllLogosForDesign(
    customerId: number,
    query: PageQuery,
  ): Promise<PagedResponse<LogoDto>> {
    const queryString = toQueryParam(query);
    const response = await authFetch(
      `${API_URL}/logos/design/${customerId}${queryString}`,
    );
    return await response.json();
  }

  async function getAllCountries(
    query: PageQuery,
  ): Promise<PagedResponse<CountryDto>> {
    const queryString = toQueryParam(query);
    const response = await authFetch(`${API_URL}/countries${queryString}`);
    return await response.json();
  }

  async function getLogo(orgId: number, logoId: number): Promise<LogoDto> {
    const response = await authFetch(
      `${API_URL}/organizations/${orgId}/logos/${logoId}`,
    );
    const data = await response.json();
    //console.log("Logo:", data);
    return data;
  }

  async function getLogoOptions(): Promise<LogoOptionsDto> {
    const response = await authFetch(`${API_URL}/logos/options/all`);

    return await response.json();
  }

  async function getTransferOptions(vendorId?: number): Promise<TransferDto[]> {
    const vendorIdQueryParameter = vendorId ? `?vendorId=${vendorId}` : "";

    const response = await authFetch(
      `${API_URL}/logos/options/transfersizes${vendorIdQueryParameter}`,
    );
    return await response.json();
  }

  async function deleteLogoById(orgId: number, logoId: number): Promise<void> {
    await authFetch(`${API_URL}/organizations/${orgId}/logos/${logoId}`, {
      method: "DELETE",
    });
  }

  async function deleteDesign(orgId: number, designId: number): Promise<void> {
    await authFetch(`${API_URL}/organizations/${orgId}/designs/${designId}`, {
      method: "DELETE",
    });
  }

  async function getStyleById(id: number): Promise<StyleDto> {
    const response = await authFetch(`${API_URL}/styles/${id}`);
    return await response.json();
  }

  async function deleteStyle(styleId: number): Promise<void> {
    await authFetch(`${API_URL}/styles/${styleId}`, {
      method: "DELETE",
    });
  }

  async function getOriginalMask(
    styleId: number,
    styleViewId: number,
  ): Promise<UrlResponse> {
    const response = await authFetch(
      `${API_URL}/styles/${styleId}/views/${styleViewId}/mask?original=true`,
    );
    return await response.json();
  }

  async function getProfile(orgId?: number): Promise<ProfileResponse> {
    const path = orgId ? `/${orgId}` : "";

    const response = await authFetch(`${API_URL}/profile${path}`);

    return await response.json();
  }

  async function updateProfile(
    profile: UpdateProfileDto,
    orgId?: number,
  ): Promise<ProfileResponse> {
    const body = profile;
    const path = orgId ? `/${orgId}` : "";

    const response = await authFetch(`${API_URL}/profile${path}`, {
      method: "PUT",
      body: JSON.stringify(body),
    });
    return response.json();
  }

  async function updateMask(
    styleId: number,
    styleViewId: number,
    mask: Blob,
  ): Promise<void> {
    const file = new File([mask], "some_file");
    const formData = new FormData();
    formData.append("file", file);

    await authFetch(`${API_URL}/styles/${styleId}/views/${styleViewId}/mask`, {
      method: "PUT",
      body: formData,
    });
  }

  async function uploadCompanyLogo(orgId: number, logo: File): Promise<void> {
    const formData = new FormData();
    formData.append("file", logo);

    await authFetch(`${API_URL}/organizations/${orgId}/logo`, {
      method: "POST",
      body: formData,
    });
  }

  async function uploadProviderLogo(logo: File): Promise<void> {
    const formData = new FormData();
    formData.append("file", logo);

    await authFetch(`${API_URL}/provider/logo`, {
      method: "POST",
      body: formData,
    });
  }

  async function uploadCatalog(
    manufacturerId: string,
    catalog: File,
  ): Promise<void> {
    const formData = new FormData();
    formData.append("File", catalog);

    await authFetch(`${API_URL}/styles/upload/${manufacturerId}`, {
      method: "POST",
      body: formData,
    });
  }

  async function createManufacturer(
    body: CreateManufacturerBody,
  ): Promise<ManufacturerDto> {
    const response = await authFetch(`${API_URL}/manufacturers`, {
      method: "POST",
      body: JSON.stringify(body),
    });

    return response.json();
  }

  async function createUser(body: CreateUserBody): Promise<void> {
    await authFetch(`${API_URL}/users/create`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  async function deleteUser(userId: number): Promise<void> {
    await authFetch(`${API_URL}/users/${userId}`, {
      method: "DELETE",
    });
  }

  async function changeStyle(
    orgId: number,
    designId: number,
    body: ChangeStyleBody,
  ): Promise<Design> {
    const response = await authFetch(
      `${API_URL}/organizations/${orgId}/designs/${designId}/style`,
      {
        method: "PUT",
        body: JSON.stringify(body),
      },
    );

    return response.json();
  }

  async function changeDesignTitle(
    orgId: number,
    designId: number,
    body: ChangeTitleBody,
  ): Promise<Design> {
    const response = await authFetch(
      `${API_URL}/organizations/${orgId}/designs/${designId}/rename`,
      {
        method: "PUT",
        body: JSON.stringify(body),
      },
    );

    return response.json();
  }

  async function getUsersOfOrganization(orgId: number): Promise<User[]> {
    const response = await authFetch(`${API_URL}/users?orgId=${orgId}`, {
      method: "GET",
    });
    return response.json();
  }

  async function getCreateMaskQueueCount(): Promise<CreateMaskQueueCount> {
    const response = await authFetch(`${API_URL}/styles/view/pending`);

    return response.json();
  }

  type QueueCountApiResponse = {
    QueueName?: string;
    queueName?: string;
    Count?: number;
    count?: number;
  };

  function mapQueueCount(
    item?: QueueCountApiResponse | null,
  ): CreateMaskQueueCount | null {
    if (!item) {
      return null;
    }
    const rawName = item.QueueName ?? item.queueName ?? "";
    const rawCount = item.Count ?? item.count ?? 0;
    return {
      queueName: typeof rawName === "string" ? rawName : String(rawName ?? ""),
      count: typeof rawCount === "number" ? rawCount : Number(rawCount) || 0,
    };
  }

  function normalizeQueueCounts(data: unknown): CreateMaskQueueCount[] {
    if (Array.isArray(data)) {
      return data
        .map((item) => mapQueueCount(item as QueueCountApiResponse))
        .filter((item): item is CreateMaskQueueCount => item !== null);
    }
    if (typeof data === "object" && data !== null) {
      const single = mapQueueCount(data as QueueCountApiResponse);
      return single ? [single] : [];
    }
    return [];
  }

  async function getImportLogosQueueCounts(): Promise<CreateMaskQueueCount[]> {
    const response = await authFetch(`${API_URL}/logos/import/pending`);
    const data = await response.json();
    return normalizeQueueCounts(data);
  }

  async function getImportDesignsQueueCounts(): Promise<
    CreateMaskQueueCount[]
  > {
    const response = await authFetch(`${API_URL}/designs/import/pending`);
    const data = await response.json();
    return normalizeQueueCounts(data);
  }

  async function updateStyle(
    styleId: number,
    body: UpdateStyleBody,
  ): Promise<StyleDto> {
    const response = await authFetch(`${API_URL}/styles/${styleId}`, {
      method: "PUT",
      body: JSON.stringify(body),
    });

    return response.json();
  }

  async function changeColors(
    orgId: Organization["id"],
    logoId: BaseLogo["id"],
    colors: ChangeLogoColors,
  ): Promise<BaseLogo> {
    const body = colors;

    const response = await authFetch(
      `${API_URL}/organizations/${orgId}/logos/${logoId}/changecolors`,
      {
        method: "PUT",
        body: JSON.stringify(body),
      },
    );
    const data = await response.json();
    // console.log("Change colors data:", data);
    return data;
  }

  async function changeLogoTitle(
    orgId: number,
    logoId: number,
    title: string,
  ): Promise<BaseLogo> {
    const response = await authFetch(
      `${API_URL}/organizations/${orgId}/logos/${logoId}/rename`,
      {
        method: "PUT",
        body: JSON.stringify({ newName: title }),
      },
    );

    return response.json();
  }

  async function placeVerdictLogo(
    logo: BaseLogo,
    verdict: Verdict,
  ): Promise<BaseLogo> {
    const orgId = logo.customer.id;
    const logoId = logo.id;
    //console.log("Send verdict request for org: " + orgId);
    const response = await authFetch(
      `${API_URL}/organizations/${orgId}/logos/${logoId}/approval`,
      {
        method: "PUT",
        body: JSON.stringify(verdict),
      },
    );
    return response.json();
  }

  async function placeVerdictDesign(
    design: Design,
    verdict: Verdict,
  ): Promise<DesignDto> {
    const orgId = design.customer.id;
    const designId = design.id;
    const response = await authFetch(
      `${API_URL}/organizations/${orgId}/designs/${designId}/approval`,
      {
        method: "PUT",
        body: JSON.stringify(verdict),
      },
    );
    return response.json();
  }

  async function placeVerdictOrder(
    order: Order,
    verdict: Verdict,
  ): Promise<Order> {
    const orgId = order.organization.id;
    const logoId = order.id;
    const response = await authFetch(
      `${API_URL}/organizations/${orgId}/orders/${logoId}/approval`,
      {
        method: "PUT",
        body: JSON.stringify(verdict),
      },
    );
    return response.json();
  }

  async function setPackagingAndDelivery(
    order: Order,
    deliveryType: DeliveryType,
    packaging: Packaging,
  ): Promise<OrderDto> {
    const orgId = order.organization.id;
    const orderId = order.id;
    const response = await authFetch(
      `${API_URL}/organizations/${orgId}/orders/${orderId}/packagingAndDelivery`,
      {
        method: "PUT",
        body: JSON.stringify({
          deliveryType: deliveryType.toString(),
          packagingType: packaging.toString(),
        }),
      },
    );
    return response.json();
  }

  async function getDeliveryDatePreview(
    orgId: number,
    productNos: string[],
    deliveryType: string,
  ): Promise<Date> {
    const response = await authFetch(
      `${API_URL}/organizations/${orgId}/orders/delivery-date-preview`,
      {
        method: "POST",
        body: JSON.stringify({
          productNos,
          deliveryType,
        }),
      },
    );

    if (!response.ok) {
      throw new Error("Failed to fetch delivery date");
    }

    const data = await response.json();
    //console.log("Delivery Date Preview API Response:", data);

    let dateStr = data;

    // Handle NAV wrapper object or DTO if present
    if (data && typeof data === "object") {
      if ("return_value" in data) {
        dateStr = data.return_value;
      } else if ("LatestDeliveryDate" in data) {
        dateStr = data.LatestDeliveryDate;
      } else if ("latestDeliveryDate" in data) {
        dateStr = data.latestDeliveryDate;
      }
    }

    const date = new Date(dateStr);
    if (isNaN(date.getTime())) {
      throw new Error(
        `Invalid date received from API. Raw data: ${JSON.stringify(data)}`,
      );
    }

    // Force date to be 12:00:00 to avoid timezone issues when converting to YYYY-MM-DD
    date.setHours(12, 0, 0, 0);

    return date;
  }

  async function getDeliveryAddresses(orgId?: number) {
    if (orgId == null) {
      return [];
    }
    const response = await authFetch(
      `${API_URL}/organizations/${orgId}/deliveryAddresses`,
    );
    return response.json();
  }

  async function setDefaultDeliveryAddress(orgId: number, addressId: number) {
    const response = await authFetch(
      `${API_URL}/organizations/${orgId}/deliveryAddress/${addressId}`,
      { method: "PUT" },
    );
  }

  async function getPublicDesignEntry(key: string) {
    const response = await fetch(`${API_URL}/public/order/entry/${key}`);
    return response.json();
  }

  async function postPublicDesignEntry(key: string, orderline: CartItemDesign) {
    const instances = orderline.instances.map((i) => ({
      label: i.label,
      size: i.size,
      quantity: i.toDelivery,
      values: i.logos
        .filter((l) => l.logoVariant !== "static")
        .map((l) => ({
          logoId: l.logoId,
          value: l.value,
        })),
    }));
    const response = await fetch(`${API_URL}/public/order/entry/${key}`, {
      method: "POST",
      body: JSON.stringify({ instances }),
    });
    return response.ok;
  }

  async function downloadPublicTemplate(
    key: string,
    orderline: CartItemDesign,
  ) {
    const design = orderline.design;
    const body: CreateDesignTemplateDto = {
      designs: [
        {
          designId: Number(design.id), // Ensure designId is a number
          instances: orderline.instances.map((instance, index) => ({
            label: `Design ${index + 1}`,
            size: instance.size ?? "",
            quantity: instance.toDelivery,
            logos: instance.logos
              .filter((l) => l.logoVariant !== "static")
              .map((logo) => ({
                logoId: logo.logoId,
                view: logo.locationIdentifier,
                value: logo.value.toString(),
              })),
          })),
        },
      ],
    };
    const response = await fetch(
      `${API_URL}/public/order/entry/download-template/${key}`,
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    );
    return response.blob();
  }

  async function uploadPublicTemplate(key: string, spreadsheet: File) {
    const file = new File([spreadsheet], "some_file");
    const formData = new FormData();
    formData.append("file", file);

    const response = await fetch(
      `${API_URL}/public/order/entry/upload-design-template/${key}`,
      {
        method: "POST",
        body: formData,
      },
    );

    return response.json();
  }

  async function approveLogoExternal(logo: ImageLogo): Promise<BaseLogo> {
    const logoPublicKey = logo.approval!.publicKey;
    const response = await authFetch(
      `${API_URL}/logos/${logoPublicKey}/approveExternal`,
      {
        method: "PUT",
      },
    );
    return response.json();
  }

  async function rejectLogoExternal(logo: BaseLogo): Promise<BaseLogo> {
    const orgId = logo.customer.id;
    const logoId = logo.id;
    const response = await authFetch(
      `${API_URL}/organizations/${orgId}/logos/${logoId}/rejectExternal`,
      {
        method: "PUT",
      },
    );
    return response.json();
  }

  async function sendApprovalLogo(
    logo: BaseLogo,
    email: string,
  ): Promise<void> {
    const orgId = logo.customer.id;
    const logoId = logo.id;
    const response = await authFetch(
      `${API_URL}/organizations/${orgId}/logos/${logoId}/approval-send`,
      {
        method: "POST",
        body: JSON.stringify({ email }),
      },
    );
    return response.json();
  }

  async function sendApprovalLogoConditional(
    logo: BaseLogo,
    email: string,
  ): Promise<void> {
    const orgId = logo.customer.id;
    const logoId = logo.id;
    const response = await authFetch(
      `${API_URL}/organizations/${orgId}/logos/${logoId}/approval-send-conditional`,
      {
        method: "POST",
        body: JSON.stringify({ email }),
      },
    );
    return response.json();
  }

  async function sendApprovalDesign(
    design: Design,
    email: string,
  ): Promise<Design> {
    const orgId = design.customer.id;
    const designId = design.id;
    const response = await authFetch(
      `${API_URL}/organizations/${orgId}/designs/${designId}/approval-send`,
      {
        method: "POST",
        body: JSON.stringify({ email }),
      },
    );
    return response.json();
  }

  async function sendApprovalDesignConditional(
    design: Design,
    email: string,
  ): Promise<Design> {
    const orgId = design.customer.id;
    const designId = design.id;
    const response = await authFetch(
      `${API_URL}/organizations/${orgId}/designs/${designId}/approval-send-conditional`,
      {
        method: "POST",
        body: JSON.stringify({ email }),
      },
    );
    return response.json();
  }

  async function generatePublicKeys(
    logoIds?: number[],
    designIds?: number[],
  ): Promise<{ publicKeys: string[] }> {
    const body: any = {};
    if (logoIds) body.logoIds = logoIds;
    if (designIds) body.designIds = designIds;

    const response = await authFetch(`${API_URL}/approval/generate-keys`, {
      method: "POST",
      body: JSON.stringify(body),
    });
    return response.json();
  }

  async function sendMultiApprovalEmail(
    publicKeys: string[],
    type: "logo" | "design",
    email: string,
  ): Promise<void> {
    const response = await authFetch(`${API_URL}/approval/send-email`, {
      method: "POST",
      body: JSON.stringify({ publicKeys, type, email }),
    });

    if (!response.ok) {
      throw new Error(`Failed to send approval email: ${response.status}`);
    }
  }

  async function initiateApprovalLogo(
    logo: BaseLogo,
    approval: InitiateApproval,
  ): Promise<BaseLogo> {
    // FIXME: Should use current user's organization id instead of the selected customer's id
    const orgId = logo.customer.id;
    const logoId = logo.id;

    const response = await authFetch(
      `${API_URL}/organizations/${orgId}/logos/${logoId}/approval-start`,
      {
        method: "POST",
        body: JSON.stringify(approval),
      },
    );
    return response.json();
  }

  async function initiateApprovalDesign(
    design: Design,
    approval: InitiateApproval,
  ): Promise<DesignDto> {
    // FIXME: Should use current user's organization id instead of the selected customer's id
    const orgId = design.customer.id;
    const designId = design.id;

    const response = await authFetch(
      `${API_URL}/organizations/${orgId}/designs/${designId}/approval-start`,
      {
        method: "POST",
        body: JSON.stringify(approval),
      },
    );
    return response.json();
  }

  async function setCommissionProfile(
    orgId: number,
    commissionProfile: CommissionProfile,
  ): Promise<CommissionProfileDto> {
    const response = await authFetch(
      `${API_URL}/organization/${orgId}/commission`,
      {
        method: "PUT",
        body: JSON.stringify(commissionProfile),
      },
    );
    return response.json();
  }

  async function deleteCommissionProfile(orgId: number): Promise<void> {
    await authFetch(`${API_URL}/organization/${orgId}/commission`, {
      method: "DELETE",
    });
  }

  async function downloadTemplate(
    orgId: number,
    designs: CartItemDesign[],
  ): Promise<Blob> {
    const body: CreateDesignTemplateDto = {
      designs: designs.map((design) => ({
        designId: Number(design.id), // Ensure designId is a number
        instances: design.instances.map((instance, index) => ({
          label: `Design ${index + 1}`,
          size: instance.size ?? "",
          quantity: instance.toDelivery,
          logos: instance.logos.map((logo) => ({
            logoId: logo.logoId,
            view: logo.locationIdentifier,
            value: logo.value.toString(),
          })),
        })),
      })),
    };

    const response = await authFetch(
      `${API_URL}/organizations/${orgId}/orders/download-template`,
      {
        method: "post",
        body: JSON.stringify(body),
      },
    );
    return await response.blob();
  }

  async function uploadDesignTemplate(
    orgId: number,
    spreadsheet: File,
  ): Promise<DesignTemplate> {
    const file = new File([spreadsheet], "some_file");
    const formData = new FormData();
    formData.append("file", file);

    const response = await authFetch(
      `${API_URL}/organizations/${orgId}/orders/upload-design-template`,
      {
        method: "POST",
        body: formData,
      },
    );

    return response.json();
  }

  async function uploadLogoTemplate(
    orgId: number,
    spreadsheet: File,
  ): Promise<LogoTemplate> {
    const file = new File([spreadsheet], "some_file");
    const formData = new FormData();
    formData.append("file", file);

    const response = await authFetch(
      `${API_URL}/organizations/${orgId}/orders/upload-logo-template`,
      {
        method: "POST",
        body: formData,
      },
    );

    return response.json();
  }

  async function reorder(orgId: number, orderId: number): Promise<OrderDto> {
    const response = await authFetch(
      `${API_URL}/organizations/${orgId}/orders/${orderId}/reorder`,
      {
        method: "POST",
      },
    );

    return response.json();
  }

  async function refreshStorageCount(logoId: number): Promise<string> {
    const response = await authFetch(
      `${API_URL}/logos/${logoId}/refresh-storage-count`,
      {
        method: "POST",
      },
    );

    return response.text();
  }

  async function getExcludedEmailDomains(): Promise<ExcludedEmailDomainDto[]> {
    const response = await authFetch(`${API_URL}/excluded-email-domains`);
    return response.json();
  }

  async function addExcludedEmailDomain(
    body: AddExcludedEmailDomainDto,
  ): Promise<ExcludedEmailDomainDto> {
    const response = await authFetch(`${API_URL}/excluded-email-domains`, {
      method: "POST",
      body: JSON.stringify(body),
    });

    const result = await response.json();

    return result;
  }

  async function deleteExcludedEmailDomain(
    body: DeleteExcludedEmailDomainDto,
  ): Promise<void> {
    await authFetch(`${API_URL}/excluded-email-domains`, {
      method: "DELETE",
      body: JSON.stringify(body),
    });
  }

  async function getOrganizationManufacturerAccess(
    orgId: number,
  ): Promise<OrganizationManufacturerAccessDto> {
    const response = await authFetch(
      `${API_URL}/organizations/${orgId}/manufacturer-access`,
    );
    return response.json();
  }

  async function updateOrganizationAccessMode(
    orgId: number,
    accessMode: "Blacklist" | "Whitelist",
  ): Promise<void> {
    await authFetch(`${API_URL}/organizations/${orgId}/access-mode`, {
      method: "PUT",
      body: JSON.stringify({
        organizationId: orgId,
        accessMode: accessMode.toLowerCase(),
      }),
    });
  }

  async function updateOrganizationManufacturerAccess(
    orgId: number,
    manufacturerId: number,
    hasAccess: boolean,
  ): Promise<void> {
    await authFetch(`${API_URL}/organizations/${orgId}/manufacturer-access`, {
      method: "POST",
      body: JSON.stringify({
        organizationId: orgId,
        manufacturerId,
        hasAccess,
      }),
    });
  }

  async function updateManufacturerRestricted(
    manufacturerId: number,
    isRestricted: boolean,
  ): Promise<void> {
    await authFetch(
      `${API_URL}/manufacturers/${manufacturerId}/restricted/${isRestricted}`,
      {
        method: "PUT",
      },
    );
  }

  async function searchAdminVendors(
    searchQuery: string,
  ): Promise<OrganizationDto[]> {
    const response = await authFetch(
      `${API_URL}/organizations/vendors/search`,
      {
        method: "POST",
        body: JSON.stringify({
          searchTerm: searchQuery,
          maxResults: 100,
        }),
      },
    );

    return await response.json();
  }

  async function validateOldCredentials(
    username: string,
    password: string,
  ): Promise<any> {
    const response = await fetch(`${API_URL}/users/validate-old-credentials`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ username: username, password: password }),
    });

    return response.json();
  }

  async function migrateFromOldWebshop(
    username: string,
    password: string,
    firstName: string,
    lastName: string,
    email: string,
    phone: string,
    customerNo: string,
  ): Promise<any> {
    const response = await fetch(`${API_URL}/users/migrate-from-old-webshop`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        username: username,
        password: password,
        firstName: firstName,
        lastName: lastName,
        email: email,
        phone: phone,
        customerNo: customerNo,
      }),
    });

    return response.json();
  }

  async function startYearlyPriceImport(
    body?: StartPriceImportRequestDto,
  ): Promise<StartPriceImportResponseDto> {
    const response = await authFetch(`${API_URL}/price-import/yearly/start`, {
      method: "POST",
      body: body ? JSON.stringify(body) : undefined,
    });
    return response.json();
  }

  async function getYearlyPriceImportStatus(): Promise<PriceImportStatusResponseDto> {
    const response = await authFetch(`${API_URL}/price-import/yearly/status`);
    return response.json();
  }

  async function getYearlyPriceImportHistory(): Promise<PriceImportHistoryResponseDto> {
    const response = await authFetch(`${API_URL}/price-import/yearly/history`);
    return response.json();
  }

  async function cancelYearlyPriceImport(): Promise<CancelPriceImportResponseDto> {
    const response = await authFetch(`${API_URL}/price-import/yearly/cancel`, {
      method: "POST",
    });
    return response.json();
  }

  async function bulkLogoImport(
    serialNumbers: string[],
  ): Promise<BulkLogoImportResponseDto> {
    const response = await authFetch(`${API_URL}/logos/import/bulk`, {
      method: "POST",
      body: JSON.stringify({ serialNumbers } as BulkLogoImportRequestDto),
    });
    return response.json();
  }

  async function bulkLogoOrganizationUpdate(
    serialNumbers: string[],
  ): Promise<BulkLogoImportResponseDto> {
    const response = await authFetch(
      `${API_URL}/logos/organizations/update/bulk`,
      {
        method: "POST",
        body: JSON.stringify({ serialNumbers } as BulkLogoImportRequestDto),
      },
    );
    return response.json();
  }

  async function getLogoSerialsForImport(
    filters: LogoSerialsFilterParams,
  ): Promise<LogoSerialsResponseDto> {
    const params = new URLSearchParams();
    if (filters.searchTerm) params.append("searchTerm", filters.searchTerm);
    if (filters.organizationName)
      params.append("organizationName", filters.organizationName);
    if (filters.classification)
      params.append("classification", filters.classification);
    if (filters.customerId)
      params.append("customerId", String(filters.customerId));
    if (filters.method) params.append("method", filters.method);

    const queryString = params.toString();
    const url = `${API_URL}/logos/import/serials${
      queryString ? `?${queryString}` : ""
    }`;
    const response = await authFetch(url);
    return response.json();
  }

  async function getDesignSerialsForImport(
    filters: DesignSerialsFilterParams,
  ): Promise<DesignSerialsResponseDto> {
    const params = new URLSearchParams();
    if (filters.searchTerm) params.append("searchTerm", filters.searchTerm);
    if (filters.organizationName)
      params.append("organizationName", filters.organizationName);
    if (filters.customerId)
      params.append("customerId", String(filters.customerId));

    const queryString = params.toString();
    const url = `${API_URL}/designs/import/serials${
      queryString ? `?${queryString}` : ""
    }`;
    const response = await authFetch(url);
    return response.json();
  }

  async function bulkDesignImport(
    serialNumbers: string[],
  ): Promise<BulkDesignImportResponseDto> {
    const body: BulkDesignImportRequestDto = { serialNumbers };
    const response = await authFetch(`${API_URL}/designs/import/bulk`, {
      method: "POST",
      body: JSON.stringify(body),
    });
    return response.json();
  }

  async function getHeatingAndPackaging(
    orgId: number,
  ): Promise<
    { heatItemNo: string; packingItemNo: string; specific: string }[]
  > {
    const response = await authFetch(
      `${API_URL}/organizations/${orgId}/navision/heating-packaging`,
    );
    return await response.json();
  }

  async function getNavisionItemPrice(
    orgId: number,
    itemNo: string,
    customerNo?: string,
  ): Promise<any> {
    const queryString = customerNo ? `?customerNo=${customerNo}` : "";
    const response = await authFetch(
      `${API_URL}/organizations/${orgId}/navision/items/${itemNo}${queryString}`,
    );
    return await response.json();
  }

  return {
    getHeatingAndPackaging,
    getNavisionItemPrice,
    changeColors,
    changeLogoTitle,
    getAllStyles,
    deleteStyle,
    getAllDesigns,
    getAllManufacturers,
    getManufacturer,
    createDesign,
    updateDesign,
    createTextLogo,
    createPlaceholderNameLogo,
    createPlaceholderNumberLogo,
    generateTextLogoPreview,
    generateNumberLogoPreview,
    getAllTextLogos,
    getDesignSnapshot,
    saveDesignSnapshot: saveDesignSnapshot,
    getDesignPriceSummary,
    getOrder: getOrder,
    submitDesign: submitDesign,
    getAllVendors: getAllVendors,
    getAllCustomers: getAllCustomers,
    getAllOrders,
    getOrganization,
    getVendor,
    getCustomer,
    getAllLogos,
    getAllLogosForDesign,
    getAllCountries,
    getLogo,
    getLogoOptions,
    getTransferOptions,
    getLogos,
    deleteLogoById,
    getStyleById,
    getOriginalMask,
    getProfile,
    updateProfile,
    createAndUploadImageLogo,
    createImageLogo,
    uploadFileImageLogo,
    updateMask,
    updateOrganization: updateOrganization,
    createOrganization: createOrganization,
    deleteOrganization: deleteOrganization,
    uploadCompanyLogo,
    uploadProviderLogo,
    uploadCatalog,
    createManufacturer,
    createUser,
    deleteUser,
    getCreateMaskQueueCount,
    getImportLogosQueueCounts,
    getImportDesignsQueueCounts,
    updateStyle,
    changeStyle,
    changeDesignTitle,
    createOrder,
    updateOrder,
    getOrderPrices,
    commitOrder,
    summarizeOrder,
    getDesign,
    updateLogo,
    resizeLogo,
    copyLogo,
    copyDesign,
    commitLogo,
    placeVerdictLogo,
    placeVerdictDesign,
    placeVerdictOrder,
    setPackagingAndDelivery,
    getDeliveryAddresses,
    getDeliveryDatePreview,
    setDefaultDeliveryAddress,
    getPublicDesignEntry,
    postPublicDesignEntry,
    downloadPublicTemplate,
    uploadPublicTemplate,
    approveLogoExternal,
    rejectLogoExternal,
    createCustomer,
    updateCustomer,
    deleteCustomer,
    initiateApprovalLogo,
    sendApprovalLogo,
    sendApprovalLogoConditional,
    sendApprovalDesign,
    sendApprovalDesignConditional,
    commitDesign,
    skipApprovalDesign,
    initiateApprovalDesign,
    deleteDesign,
    getUsersOfOrganization,
    setCommissionProfile,
    deleteCommissionProfile,
    downloadTemplate,
    uploadDesignTemplate,
    uploadLogoTemplate,
    reorder,
    refreshStorageCount,
    generatePublicKeys,
    sendMultiApprovalEmail,
    getExcludedEmailDomains,
    addExcludedEmailDomain,
    deleteExcludedEmailDomain,
    getOrganizationManufacturerAccess,
    updateOrganizationAccessMode,
    updateOrganizationManufacturerAccess,
    updateManufacturerRestricted,
    searchAdminVendors,
    validateOldCredentials,
    migrateFromOldWebshop,
    startYearlyPriceImport,
    getYearlyPriceImportStatus,
    getYearlyPriceImportHistory,
    cancelYearlyPriceImport,
    bulkLogoImport,
    bulkLogoOrganizationUpdate,
    getLogoSerialsForImport,
    getDesignSerialsForImport,
    bulkDesignImport,
    getDesignPrices,
    assignCustomer,
    assignCustomerToDesign,
  };

  async function assignCustomer(
    orgId: number,
    logoId: number,
    customerId: number,
  ): Promise<LogoDto> {
    const response = await authFetch(
      `${API_URL}/organizations/${orgId}/logos/${logoId}/assign-customer`,
      {
        method: "POST",
        body: JSON.stringify({ customerId }),
      },
    );
    return response.json();
  }

  async function assignCustomerToDesign(
    orgId: number,
    designId: number,
    customerId: number,
  ): Promise<DesignDto> {
    const response = await authFetch(
      `${API_URL}/organizations/${orgId}/designs/${designId}/assign-customer`,
      {
        method: "POST",
        body: JSON.stringify({ customerId }),
      },
    );
    return response.json();
  }

  async function getDesignPrices(designIds: number[]): Promise<DesignDto[]> {
    const response = await authFetch(
      `${API_URL}/organizations/designs/prices`,
      {
        method: "POST",
        body: JSON.stringify(designIds),
      },
    );
    return await response.json();
  }
}
